/**
 * OnboardingRitualCard
 *
 * Matches the platform's RitualRecommendationCard design.
 * Images resolved via getAssetUrl().
 */

import type { OnboardingRitual } from "@shared/types"
import { Clock } from "lucide-react"
import { useNavigate } from "react-router-dom"
import aiAvatarImg from "@/assets/icons/ai-conversation-avatar.png"
import { BookmarkIcon } from "@/components/icons"
import { cn } from "@/lib/utils"
import { getAssetUrl } from "@/utils/assets"

interface OnboardingRitualCardProps {
  ritual: OnboardingRitual
}

export function OnboardingRitualCard({ ritual }: OnboardingRitualCardProps) {
  const navigate = useNavigate()

  const raw = ritual as unknown as Record<string, unknown>
  const coverFile = (raw.coverImageAsset || raw.coverImage) as string | undefined
  const coverUrl = coverFile
    ? coverFile.startsWith("http")
      ? coverFile
      : getAssetUrl(coverFile)
    : ""

  const org = raw.organizers as Array<Record<string, unknown>> | undefined
  const first = org?.[0]
  const orgName = (first?.name as string) || "Rituality"

  return (
    <div className="flex flex-col gap-2.5 max-w-[480px]">
      <div
        className={cn(
          "relative w-[480px] h-[200px] overflow-hidden rounded-tl-[40px] rounded-br-[40px] border border-[#2c2c2c]",
        )}
      >
        <img
          src={coverUrl}
          alt={ritual.title}
          className="absolute inset-0 h-full w-full object-cover"
        />

        <div className="absolute top-4 left-5 right-5 z-10 flex items-start justify-between">
          {orgName ? (
            <div className="flex items-center gap-2 min-w-0">
              <img
                src={aiAvatarImg}
                alt=""
                className="size-[30px] rounded-full object-cover shrink-0"
              />
              <span className="text-[13px] font-semibold text-[#f2f0eb]/90 leading-normal truncate">
                {orgName}
              </span>
            </div>
          ) : (
            <div />
          )}
          <div
            className="shrink-0 rounded-[10px] border border-[rgba(242,240,235,0.15)] bg-[rgba(242,240,235,0.05)] p-2.75"
            style={{ boxShadow: "0 5px 10px 0 rgba(2,2,2,0.3)" }}
          >
            <BookmarkIcon size={22} color="#f2f0eb" />
          </div>
        </div>

        <div
          className="absolute inset-x-0 bottom-0 h-[70px] z-10"
          style={{ background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)" }}
        />

        <div className="absolute inset-x-0 bottom-0 px-5 pb-4 flex flex-col gap-1.5 z-20">
          <h4 className="text-[18px] font-semibold text-[#f2f0eb]/90 leading-tight line-clamp-1">
            {ritual.title}
          </h4>
          <div className="flex items-center gap-2.5 text-[13px]">
            <span className="rounded-[5px] px-1.5 py-0.5 text-[13px] font-medium leading-5 opacity-90 bg-olive text-[#f2f0eb]">
              Free
            </span>
            <span className="text-[#f2f0eb]/70 leading-5">Self-Guided</span>
            <span className="flex items-center gap-1 text-[#f2f0eb]/70 leading-5">
              <Clock className="size-3" />
              {ritual.duration} min
            </span>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => navigate(`/rituals/${ritual.id}?tour=ritual`)}
        className="w-[210px] h-[50px] rounded-[100px] bg-[#f2f0eb] text-[#020202] text-[14px] leading-5 font-semibold font-montserrat uppercase hover:bg-[#f2f0eb]/90 transition-colors flex items-center justify-center"
        style={{ boxShadow: "0px 5px 10px rgba(2,2,2,0.3)" }}
      >
        Start Ritual
      </button>
    </div>
  )
}
